import React, { useState } from "react";
import { ChevronDown, ChevronUp, Sparkles, Plus, Coffee } from "lucide-react";
import clsx from "clsx";
import { fmtDuration } from "@/lib/format";
import ContextMenu from "./grid/ContextMenu";

export default function FloatingTaskTray({ floatingTasks = [], collapsed, onToggle, onSchedule, onNewFloating, onAddMeal, onClickTask, onDuplicateTask, onDeleteTask }) {
  const [ctxMenu, setCtxMenu] = useState(null); // {x, y, task}

  const ctxItems = ctxMenu ? [
    { key: "open", label: "Open details", onClick: () => onClickTask && onClickTask(ctxMenu.task) },
    { key: "duplicate", label: "Duplicate task", onClick: () => onDuplicateTask && onDuplicateTask(ctxMenu.task), disabled: !onDuplicateTask },
    { divider: true },
    { key: "delete", label: "Delete task", tone: "danger", onClick: () => onDeleteTask && onDeleteTask(ctxMenu.task), disabled: !onDeleteTask },
  ] : [];

  return (
    <div
      className={clsx(
        "bg-bg-surface border-b border-line transition-all",
        collapsed ? "py-1" : "py-2"
      )}
      data-testid="floating-tray"
    >
      <div className="flex items-center px-3 gap-3">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 text-ink-dim hover:text-amber transition-colors"
          data-testid="floating-tray-toggle"
        >
          {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          <span className="mono text-2xs uppercase tracking-widest">
            FLOATING ·{" "}
            <span className="text-amber">{floatingTasks.length}</span>{" "}
            {floatingTasks.length === 1 ? "task" : "tasks"}
          </span>
        </button>

        {!collapsed && (
          <div className="flex items-center gap-2 overflow-x-auto flex-1">
            {floatingTasks.length === 0 && (
              <span className="mono text-2xs text-ink-mute uppercase tracking-wider">— no floating tasks —</span>
            )}
            {floatingTasks.map((t) => (
              <div
                key={t.id}
                className="float-card flex items-center gap-2 cursor-pointer"
                draggable
                onClick={(e) => {
                  if (e.defaultPrevented) return;
                  onClickTask && onClickTask(t);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setCtxMenu({ x: e.clientX, y: e.clientY, task: t });
                }}
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", JSON.stringify({ kind: "floating-task", task_id: t.id }));
                  e.dataTransfer.effectAllowed = "move";
                }}
                data-testid={`floating-card-${t.id}`}
                title="Click to open · right-click for actions · drag onto schedule"
              >
                <span className="truncate max-w-[200px]">{t.title}</span>
                <span className="mono text-2xs text-violet/70">{fmtDuration(t.duration_min)}</span>
              </div>
            ))}
            <button
              onClick={() => onNewFloating && onNewFloating()}
              className="btn-ghost ml-auto shrink-0"
              data-testid="new-floating-btn"
              title="Add a task that isn't placed on the calendar yet"
            >
              <Plus className="w-3 h-3" /> NEW FLOATING
            </button>
            {onAddMeal && (
              <button
                onClick={() => onAddMeal()}
                className="btn-ghost shrink-0"
                data-testid="add-meal-btn"
                title="Add a meal break — 60min, all crew by default"
              >
                <Coffee className="w-3 h-3" /> MEAL
              </button>
            )}
            {floatingTasks.length > 0 && (
              <button
                onClick={() => onSchedule && onSchedule(floatingTasks[0])}
                className="btn-ghost shrink-0"
                title="AI auto-place all (coming soon)"
                disabled
              >
                <Sparkles className="w-3 h-3" />
                AI PLACE
              </button>
            )}
          </div>
        )}
      </div>
      <ContextMenu
        open={!!ctxMenu}
        x={ctxMenu?.x}
        y={ctxMenu?.y}
        onClose={() => setCtxMenu(null)}
        items={ctxItems}
      />
    </div>
  );
}
